import { ChevronDown, ExternalArrow } from '@app/components/icons/ui-icons';
import Passage from '@app/components/ui/passage';
import { BunnyFace } from '@app/components/ui/bunny';
import { ProjectDto } from '@app/models/dtos/contentDto';
import cn from 'classnames';
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import LazyImage from '@app/components/ui/lazy-image';
import { useRef, useState } from 'react';

/** Each card pins a little lower than the one before, so the deck's edges stay visible. */
const STICK_TOP = 56;
const STICK_STEP = 12;

function Preview({ project }: { project: ProjectDto }) {
    if (project.image) {
        // The bucket screenshots inset the real UI in the middle of a coloured
        // canvas, so a top crop shows only padding.
        return <LazyImage src={project.image} alt={`${project.title} preview`} fill sizes="(max-width: 768px) 100vw, 680px" className="object-cover object-center" loaderSize={28} />;
    }

    // No public preview — say so plainly rather than faking a screenshot.
    return (
        <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(120%_140%_at_20%_0%,var(--accent-soft)_0%,transparent_65%)]">
            <span className="t-label text-muted">No public preview</span>
        </div>
    );
}

interface CardProps {
    project: ProjectDto;
    index: number;
    isLast: boolean;
    /** Set on cards revealed by the button, so they fall in one after another. */
    dropDelay?: number;
}

function Card({ project, index, isLast, dropDelay }: CardProps) {
    const ref = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    // The card eases back a touch as the next one rides up over it.
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.12', 'end 0.08'] });
    const scale = useTransform(scrollYProgress, [0, 1], [1, isLast ? 1 : 0.975]);

    const body = (
        <>
            {/* Title first: a pinned card still says what it is once the next one covers its preview. */}
            <div className="flex items-baseline justify-between gap-4 px-5 pb-3 pt-4">
                <h3 className="t-title inline-flex items-baseline gap-1.5 text-ink">
                    {project.title}
                    {project.link && <ExternalArrow size={12} className="text-muted transition-colors group-hover:text-accent" />}
                </h3>
                <span className="t-label shrink-0 text-muted">{String(index + 1).padStart(2, '0')}</span>
            </div>
            <p className="t-small px-5 pb-4 text-body">{project.blurb}</p>
            <div className="relative h-[190px] w-full overflow-hidden border-t border-hairline bg-paper">
                <Preview project={project} />
            </div>
        </>
    );

    const drop = dropDelay === undefined || reduced ? {} : { initial: { opacity: 0, y: -26 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: dropDelay, ease: [0.22, 1, 0.36, 1] as const } };

    return (
        <motion.li className="sticky" style={{ top: STICK_TOP + index * STICK_STEP }} {...drop}>
            <motion.div ref={ref} style={reduced ? undefined : { scale }} className="origin-top">
                <div className={cn('overflow-hidden rounded-xl border border-hairline bg-paper transition-colors', project.link && 'hover:border-accent/45 group')}>
                    {project.link ? (
                        <a href={project.link} target="_blank" rel="noreferrer noopener" className="block">
                            {body}
                        </a>
                    ) : (
                        <div>{body}</div>
                    )}
                </div>
            </motion.div>
        </motion.li>
    );
}

/** The hung pile: card edges peeking out, waiting to be dropped. */
function PileButton({ count, onClick }: { count: number; onClick: () => void }) {
    const reduced = useReducedMotion();

    return (
        <motion.button
            type="button"
            onClick={onClick}
            className="group mt-4 block w-full text-left"
            aria-label={`Show ${count} more projects`}
            initial={reduced ? undefined : { opacity: 0, y: 8 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -10, transition: { duration: 0.2 } }}
        >
            <span className="relative block">
                <span className="absolute -top-[22px] right-8 block h-[22px] overflow-hidden transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true">
                    <BunnyFace size={26} />
                </span>
            </span>
            <span className="mx-10 block h-2 rounded-t-lg border border-b-0 border-hairline bg-paper opacity-40 transition-all duration-300 group-hover:mx-12" />
            <span className="mx-5 block h-2 rounded-t-lg border border-b-0 border-hairline bg-paper opacity-70 transition-all duration-300 group-hover:mx-7" />
            <span className="group-hover:border-accent/45 flex items-center justify-between gap-3 rounded-xl border border-hairline bg-paper px-5 py-4 transition-colors">
                <span className="t-small text-body transition-colors group-hover:text-ink">Bunny is hiding {count} more</span>
                <ChevronDown size={16} className="text-muted transition-transform duration-300 group-hover:translate-y-0.5 group-hover:text-accent" />
            </span>
        </motion.button>
    );
}

export default function Work({ projects }: { projects: ProjectDto[] }) {
    const [expanded, setExpanded] = useState(false);
    const featured = projects.filter((project) => project.featured);
    const rest = projects.filter((project) => !project.featured);

    // One list, always — sticky children only stay pinned within their own parent,
    // so splitting the deck across two lists would break the stack.
    const shown = expanded ? [...featured, ...rest] : featured;

    return (
        <Passage id="work" heading="Things I built">
            <ul className="space-y-4">
                {shown.map((project, index) => (
                    <Card key={project.id} project={project} index={index} isLast={index === shown.length - 1} dropDelay={expanded && index >= featured.length ? (index - featured.length) * 0.08 : undefined} />
                ))}
            </ul>

            <AnimatePresence initial={false}>{!expanded && rest.length > 0 && <PileButton key="pile" count={rest.length} onClick={() => setExpanded(true)} />}</AnimatePresence>
        </Passage>
    );
}
